
// src/components/Sales/SalesSummaryDialog.jsx
import * as React from 'react';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import { CircularProgress } from '@mui/material';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const handlePrint = () => {
  window.print();
};

const formatMoney = (value) => {
  const num = Number(value) || 0;
  return num.toFixed(2);
};

// const getDisplayBrandName = (brand, generic) => {
//   const b = (brand ?? '').trim();
//   const g = (generic ?? '').trim();

//   const brandIsStarsOnly = b !== '' && /^[*\s]+$/.test(b);

//   if (!b || brandIsStarsOnly) return g || 'Item';
//   return b;
// };

export default function SaleSummaryDialog({ open, onClose, sale }) {
  const user = useSelector((state) => state.user);
  if (!sale) {
  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invoice</DialogTitle>
      <DialogContent dividers sx={{display:'flex', justifyContent:'center', flexDirection:'column', alignItems:'center'}}>
        <CircularProgress sx={{margin:'auto'}}/>
        <Typography sx={{ py: 4, textAlign: 'center' }}>
          Loading invoice…
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </BootstrapDialog>
  );
}

  const items = Array.isArray(sale.items) ? sale.items : [];
  const refNo = sale.refNo || sale.referenceNo || sale._id || '—';
  const createdAt = sale.createdAt ? new Date(sale.createdAt) : null;

  // const pharmacyName =
  // user?.customFields?.['pharmacy-name'] ||
  // user?.pharmacyName ||
  // 'Pharmacy';

const address =
  user?.customFields?.['address'] ||
  user?.address ||
  '';

const phone =
  user?.customFields?.['phone-number'] ||
  user?.tel ||
  '';


  // ✅ Use stored line totals if available (these already include item-level discounts)

  const computedSubtotal = Number(sale.subTotal) || items.reduce((s, l) => s + Number(l.lineTotal ?? 0), 0);
const billDiscAmt = Number(sale.billDiscountAmount ?? 0);
const billDiscPct = Number(sale.billDiscountPercent ?? 0);
const total = Number(sale.total ?? Math.max(0, computedSubtotal - billDiscAmt));
const paymentMethod = String(sale.paymentMethod || '').toLowerCase();
const cashReceived = Number(sale.cashReceived ?? 0);
const changeDue = Number(sale.changeDue ?? 0);

// fallback: if you stored cashReceived but not changeDue
const computedChange = cashReceived ? cashReceived - total : 0;
const finalChangeDue = changeDue || computedChange;


  return (
    <BootstrapDialog
      onClose={onClose}
      aria-labelledby="sale-summary-dialog-title"
      open={open}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="sale-summary-dialog-title">
        Invoice
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers id="print-receipt">
        <Box className="receipt">
          {/* Header / pharmacy info */}
          <Box className="receipt-header">
            <Typography className="receipt-title" component="div">
              ABC Pharmacy
            </Typography>
            <Typography className="receipt-subtitle" component="div">
              Pharmacy Sales Receipt
            </Typography>
            <Typography
              className="receipt-subtitle"
              component="div"
            >
              {address}
            </Typography>
            <Typography className="receipt-subtitle" component="div">
              {phone}
            </Typography>
          </Box>

          {/* Meta info */}
          <Box className="receipt-meta">
            <Box className="receipt-meta-row">
              <Typography variant="body2">Ref:</Typography>
              <Typography variant="body2" fontWeight={600}>
                {refNo}
              </Typography>
            </Box>
            {createdAt && (
              <Box className="receipt-meta-row">
                <Typography variant="body2">Date:</Typography>
                <Typography variant="body2">
                  {createdAt.toLocaleDateString()}
                </Typography>
              </Box>
            )}
            {createdAt && (
              <Box className="receipt-meta-row">
                <Typography variant="body2">Time:</Typography>
                <Typography variant="body2">
                  {createdAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Items table */}
          <Box className="receipt-items">
            <Box className="receipt-items-header">
              <Typography variant="body2">Item</Typography>
              <Typography variant="body2" align="right">
                Qty
              </Typography>
              <Typography variant="body2" align="right">
                Price
              </Typography>
              <Typography variant="body2" align="right">
                Total
              </Typography>
            </Box>

            {items.length === 0 && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                No line items found.
              </Typography>
            )}

            {items.map((line, idx) => {
              const qty = Number(line.qty ?? line.quantity ?? 1);

              // ✅ Show discounted unit price if present, otherwise fallback to unitPrice
              const unit = Number(
                line.unitPriceAfterDiscount ?? line.unitPrice ?? line.price ?? 0
              );

              // ✅ Use stored lineTotal if present (most accurate)
              const lineTotal = Number(line.lineTotal ?? qty * unit);

              const name =
                line.brandName ||
                line.name ||
                line.medicineName ||
                'Item';
                

              // (Not changing layout) — just compute it correctly
              return (
                <Box key={line._id || idx} className="receipt-item-row">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" className="receipt-item-name">
                    {name}
                  </Typography>
                  {Number(line.discount ?? 0) > 0 && (
      <Typography
        variant="caption"
        sx={{ display: "block", opacity: 0.7, marginTop: "2px" }}
      >
        {Number(line.discount)}% off
      </Typography>
    )}
    </Box>
                  <Typography variant="body2" align="right">
                    {qty}
                  </Typography>
                  <Typography variant="body2" align="right">
                    {formatMoney(unit)}
                  </Typography>
                  <Typography variant="body2" align="right">
                    {formatMoney(lineTotal)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Totals */}
          <Box className="receipt-totals">
            <Box className="receipt-totals-row">
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">
                {formatMoney(computedSubtotal)}
              </Typography>
            </Box>

            {billDiscAmt > 0 && (
          <Box className="receipt-totals-row">
            <Typography variant="body2">
              Discount {billDiscPct > 0 ? `(${billDiscPct}%)` : ""}
            </Typography>
            <Typography variant="body2">-{formatMoney(billDiscAmt)}</Typography>
          </Box>
        )}


            <Box className="receipt-totals-row" sx={{paddingBottom:'4px', borderBottom:'1px dashed #333'}}>
              <Typography variant="subtitle2">Total</Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {formatMoney(total)}
              </Typography>
            </Box>
            {paymentMethod === 'cash' && cashReceived > 0 && (
  <>
    <Box className="receipt-totals-row" sx={{ mt: 1 }}>
      <Typography variant="body2">Cash received</Typography>
      <Typography variant="body2">{formatMoney(cashReceived)}</Typography>
    </Box>

    <Box className="receipt-totals-row">
      <Typography variant="body2">Change</Typography>
      <Typography variant="body2">{formatMoney(Math.max(0, finalChangeDue))}</Typography>
    </Box>
  </>
)}


          </Box>

          {/* Footer */}
          <Box className="receipt-footer">
            <Typography component="div" sx={{mt:2}}>
              Thank you for your purchase!
            </Typography>
            <Typography  variant="body2" component="div" sx={{mt:1}}>
              * Medicines once sold will not be refunded.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handlePrint} variant="contained" color="primary">
          Print
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
